import {
  confirmResetPassword,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword,
  type SignUpInput,
} from 'aws-amplify/auth';
import { useAuthSessionStore } from '../stores/auth-session-store';
import { clearTokenCache, getSessionTokens } from './token.service';

/**
 * Thin, typed wrappers over the Amplify auth calls both portals need.
 *
 * These are **plumbing, not flow**: each portal's own `features/auth` owns its
 * screens, validation, step order and error copy. Nothing here renders anything,
 * and nothing here decides where to navigate afterwards.
 */

export const signInWithPassword = async (username: string, password: string) => {
  const result = await signIn({ username, password });
  // The cache may hold a stale "no session" from before this call.
  clearTokenCache();
  await getSessionTokens({ forceRefresh: true });
  return result;
};

export const signUpUser = (input: SignUpInput) => signUp(input);

export const confirmSignUpCode = (username: string, confirmationCode: string) =>
  confirmSignUp({ username, confirmationCode });

export const resendConfirmationCode = (username: string) => resendSignUpCode({ username });

export const requestPasswordReset = (email: string) => resetPassword({ username: email });

export const submitPasswordReset = (email: string, confirmationCode: string, newPassword: string) =>
  confirmResetPassword({ username: email, confirmationCode, newPassword });

export const changePassword = (oldPassword: string, newPassword: string) =>
  updatePassword({ oldPassword, newPassword });

export const signInWithProvider = (provider?: string) =>
  signInWithRedirect({ provider: provider ? { custom: provider } : undefined });

/**
 * Signs out globally and drops every cached token.
 *
 * `global: true` matches the legacy behaviour: it revokes the refresh token
 * server-side, so signing out in one tab genuinely ends the session rather than
 * leaving other tabs able to refresh their way back in.
 *
 * The session store is reset **before** the network call, not after. It is a mirror of what
 * Amplify holds, and the guards read the mirror: left saying `authenticated`, `PublicRoute`
 * bounces the sign-in page straight back to the dashboard, so the user appears not to have
 * signed out until a hard refresh re-reads Amplify.
 */
export const signOutUser = async (): Promise<void> => {
  clearTokenCache();
  useAuthSessionStore.getState().setUnauthenticated();
  await signOut({ global: true });
};
